import React, { useEffect, useRef } from 'react';
import {
  BaseComponentProps,
  WithChildrenProps,
} from '../types/componentThemes';
import { useReducedMotion } from '../hooks/useReducedMotion';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps
  extends BaseComponentProps,
    Omit<WithChildrenProps, 'children'> {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal size variant */
  size?: ModalSize;
  /** Optional header content */
  header?: React.ReactNode;
  /** Main modal content */
  children?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Whether clicking overlay closes modal */
  closeOnOverlayClick?: boolean;
  /** Whether ESC key closes modal */
  closeOnEsc?: boolean;
}

/**
 * Modal Component
 * 
 * A modal dialog component with overlay, focus management, and animations.
 * Supports multiple sizes and configurable close behavior.
 * 
 * @example
 * ```tsx
 * <Modal isOpen={isOpen} onClose={handleClose} title="Modal Title">
 *   <p>Modal content</p>
 * </Modal>
 * 
 * <Modal 
 *   isOpen={isOpen} 
 *   onClose={handleClose}
 *   size="lg"
 *   header={<h2>Custom Header</h2>}
 *   footer={<Button onClick={handleClose}>Close</Button>}
 * >
 *   <p>Modal body content</p>
 * </Modal>
 * ```
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  header,
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  className = '',
  style,
  testId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  // Size-specific classes
  const sizeClasses: Record<ModalSize, string> = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  // Get all focusable elements within the modal
  const getFocusableElements = (): HTMLElement[] => {
    if (!modalRef.current) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    
    return Array.from(modalRef.current.querySelectorAll(focusableSelectors));
  };

  // Handle Tab key for focus trap
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEsc, onClose]);

  // Store and restore focus
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      // Focus the first focusable element in the modal
      setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 0);
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore focus to previous element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
      
      // Restore body scroll
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Overlay classes with fade animation
  const overlayClasses = [
    'fixed',
    'inset-0',
    'bg-black',
    'bg-opacity-50',
    'z-50',
    'flex',
    'items-center',
    'justify-center',
    'p-4',
    !prefersReducedMotion && 'animate-fadeIn',
  ].filter(Boolean).join(' ');

  // Modal classes with fade and scale animation
  const modalClasses = [
    'bg-white',
    'dark:bg-neutral-800',
    'rounded-lg',
    'shadow-xl',
    'w-full',
    sizeClasses[size],
    'max-h-[90vh]',
    'flex',
    'flex-col',
    !prefersReducedMotion && 'animate-modalIn',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={overlayClasses}
      onClick={handleOverlayClick}
      data-testid={testId ? `${testId}-overlay` : 'modal-overlay'}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? `${testId || 'modal'}-title` : undefined}
    >
      <div
        ref={modalRef}
        className={modalClasses}
        style={style}
        data-testid={testId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {/* Header Section */}
        {(header || title) && (
          <div
            className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between"
            data-testid={testId ? `${testId}-header` : 'modal-header'}
          >
            {header || (
              <h2
                id={`${testId || 'modal'}-title`}
                className="text-xl font-semibold text-neutral-900 dark:text-neutral-100"
              >
                {title}
              </h2>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Close modal"
              data-testid={testId ? `${testId}-close-button` : 'modal-close-button'}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Body Section */}
        {children && (
          <div
            className="px-6 py-4 overflow-y-auto flex-1"
            data-testid={testId ? `${testId}-body` : 'modal-body'}
          >
            {children}
          </div>
        )}

        {/* Footer Section */}
        {footer && (
          <div
            className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700"
            data-testid={testId ? `${testId}-footer` : 'modal-footer'}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
